// ============================================
// Deterministic Safety Checker
// Runs BEFORE and AFTER LLM protocol generation.
// This is NOT LLM-dependent — it's hard-coded logic.
// ============================================

import type { SafetyRule, SafetyViolation, SafetyCheckResult, Product } from '../../types/formulary';
import { CRITICAL_SAFETY_RULES } from '../../data/criticalSafetyRules';

interface SafetyCheckInput {
  /** Products being recommended */
  recommendedProducts: Product[];
  /** User's current medications (lowercase) */
  userMedications: string[];
  /** User's medical conditions (lowercase) */
  userConditions: string[];
  /** User's allergies (lowercase) */
  userAllergies: string[];
  /** User's sex */
  userSex?: 'female' | 'male';
  /** User's age */
  userAge?: number;
  /** Is user pregnant or trying to conceive */
  isPregnant?: boolean;
  /** Additional safety rules from Firestore (merged with critical rules) */
  firestoreRules?: SafetyRule[];
}

/**
 * Run all safety checks against recommended products.
 * Returns structured result with blocks, warnings, and info.
 */
export function runSafetyChecks(input: SafetyCheckInput): SafetyCheckResult {
  const {
    recommendedProducts,
    userMedications,
    userConditions,
    userAllergies,
    userSex,
    userAge,
    isPregnant,
    firestoreRules = [],
  } = input;

  // Merge critical (always-on) rules with Firestore rules
  const allRules = [...CRITICAL_SAFETY_RULES, ...firestoreRules].filter((r) => r.isActive);

  // Deduplicate by ID (critical rules take precedence)
  const ruleMap = new Map<string, SafetyRule>();
  for (const rule of allRules) {
    if (!ruleMap.has(rule.id)) {
      ruleMap.set(rule.id, rule);
    }
  }

  const violations: SafetyViolation[] = [];
  const warnings: SafetyViolation[] = [];
  const info: SafetyViolation[] = [];

  const productIds = new Set(recommendedProducts.map((p) => p.id));
  const normalizedMeds = userMedications.map((m) => m.toLowerCase().trim());
  const normalizedConditions = userConditions.map((c) => c.toLowerCase().trim());
  const normalizedAllergies = userAllergies.map((a) => a.toLowerCase().trim());

  for (const rule of ruleMap.values()) {
    const triggered = checkRule(rule, {
      productIds,
      normalizedMeds,
      normalizedConditions,
      normalizedAllergies,
      isPregnant: isPregnant ?? false,
    });

    if (!triggered) continue;

    // Find which recommended products are affected
    const affectedProducts = rule.affectedProductIds.length === 0
      ? recommendedProducts.map((p) => p.name) // Global rule
      : recommendedProducts
          .filter((p) => rule.affectedProductIds.includes(p.id))
          .map((p) => p.name);

    // Only flag if affected products are actually in the recommendation
    if (rule.affectedProductIds.length > 0 && affectedProducts.length === 0) {
      continue;
    }

    const violation: SafetyViolation = {
      ruleId: rule.id,
      ruleName: rule.name,
      severity: rule.severity,
      message: rule.message,
      affectedProducts,
    };

    switch (rule.severity) {
      case 'block':
        violations.push(violation);
        break;
      case 'warn':
        warnings.push(violation);
        break;
      case 'info':
        info.push(violation);
        break;
    }
  }

  // Additional deterministic checks (not from rules DB)
  checkGenderMismatch(recommendedProducts, userSex, warnings);
  checkAgeLimits(recommendedProducts, userAge, warnings);
  checkDuplicateMechanisms(recommendedProducts, warnings);

  return {
    passed: violations.length === 0,
    violations,
    warnings,
    info,
  };
}

// --- Rule matching ---

interface RuleContext {
  productIds: Set<string>;
  normalizedMeds: string[];
  normalizedConditions: string[];
  normalizedAllergies: string[];
  isPregnant: boolean;
}

function checkRule(rule: SafetyRule, ctx: RuleContext): boolean {
  // Check if any affected products are in the recommendation
  const hasAffectedProduct = rule.affectedProductIds.length === 0 ||
    rule.affectedProductIds.some((id) => ctx.productIds.has(id));

  if (!hasAffectedProduct) return false;

  switch (rule.triggerType) {
    case 'drug_interaction':
      return matchesAny(rule.matchMedications, ctx.normalizedMeds);

    case 'condition':
      return matchesAny(rule.matchConditions, ctx.normalizedConditions);

    case 'allergy':
      return matchesAny(rule.matchAllergens, ctx.normalizedAllergies);

    case 'pregnancy':
      return ctx.isPregnant || matchesAny(rule.matchConditions, ctx.normalizedConditions);

    case 'combination':
      // All specified products must be present
      return rule.matchProductIds
        ? rule.matchProductIds.every((id) => ctx.productIds.has(id))
        : false;

    case 'gender':
    case 'age':
    case 'duplicate':
      // These are handled by dedicated checks below
      return false;

    default:
      return false;
  }
}

/** Check if any item in matchList fuzzy-matches any item in userList */
function matchesAny(matchList: string[] | undefined, userList: string[]): boolean {
  if (!matchList || matchList.length === 0) return false;
  return matchList.some((match) =>
    userList.some((user) =>
      user.includes(match.toLowerCase()) || match.toLowerCase().includes(user),
    ),
  );
}

// --- Deterministic checks ---

function checkGenderMismatch(
  products: Product[],
  userSex: 'female' | 'male' | undefined,
  warnings: SafetyViolation[],
): void {
  if (!userSex) return;

  for (const product of products) {
    if (product.gender !== 'any' && product.gender !== userSex) {
      warnings.push({
        ruleId: `gender-mismatch-${product.id}`,
        ruleName: 'Gender Mismatch',
        severity: 'warn',
        message: `${product.name} is typically indicated for ${product.gender} patients. Please confirm this is intended for a ${userSex} patient.`,
        affectedProducts: [product.name],
      });
    }
  }
}

function checkAgeLimits(
  products: Product[],
  userAge: number | undefined,
  warnings: SafetyViolation[],
): void {
  if (userAge === undefined) return;

  for (const product of products) {
    if (product.ageMin !== undefined && userAge < product.ageMin) {
      warnings.push({
        ruleId: `age-min-${product.id}`,
        ruleName: 'Below Minimum Age',
        severity: 'warn',
        message: `${product.name} is typically indicated for patients ${product.ageMin}+ years old. Patient is ${userAge}.`,
        affectedProducts: [product.name],
      });
    }
    if (product.ageMax !== undefined && userAge > product.ageMax) {
      warnings.push({
        ruleId: `age-max-${product.id}`,
        ruleName: 'Above Maximum Age',
        severity: 'warn',
        message: `${product.name} is typically indicated for patients under ${product.ageMax} years old. Patient is ${userAge}.`,
        affectedProducts: [product.name],
      });
    }
  }
}

/** Check for duplicate mechanisms (e.g., two androgens, two estrogens, overlapping supplements) */
function checkDuplicateMechanisms(
  products: Product[],
  warnings: SafetyViolation[],
): void {
  const subcategoryGroups: Record<string, Product[]> = {};

  for (const product of products) {
    const key = product.subcategory;
    if (!subcategoryGroups[key]) subcategoryGroups[key] = [];
    subcategoryGroups[key].push(product);
  }

  // Flag subcategories with multiple products (potential duplicates)
  const duplicateSubcategories = [
    'hrt_male', 'hrt_female', 'thyroid', 'glp1_weight',
    'sexual_male', 'sexual_female', 'lipid',
  ];

  for (const subcat of duplicateSubcategories) {
    const group = subcategoryGroups[subcat];
    if (group && group.length > 1) {
      warnings.push({
        ruleId: `duplicate-${subcat}`,
        ruleName: 'Duplicate Mechanism',
        severity: 'warn',
        message: `Multiple ${subcat.replace(/_/g, ' ')} products recommended: ${group.map((p) => p.name).join(', ')}. Verify this combination is intended.`,
        affectedProducts: group.map((p) => p.name),
      });
    }
  }
}

// --- Pre-filter (before LLM) ---

/**
 * Filter products BEFORE sending to the LLM.
 * Removes products that are blocked by user's meds, conditions, allergies, or demographics.
 */
export function preFilterProducts(
  allProducts: Product[],
  userMedications: string[],
  userConditions: string[],
  userAllergies: string[],
  userSex?: 'female' | 'male',
  isPregnant?: boolean,
  firestoreRules?: SafetyRule[],
): Product[] {
  const allRules = [...CRITICAL_SAFETY_RULES, ...(firestoreRules ?? [])].filter(
    (r) => r.isActive && r.severity === 'block',
  );

  const normalizedMeds = userMedications.map((m) => m.toLowerCase().trim());
  const normalizedConditions = userConditions.map((c) => c.toLowerCase().trim());
  const normalizedAllergies = userAllergies.map((a) => a.toLowerCase().trim());

  // Build set of blocked product IDs
  const blockedIds = new Set<string>();

  for (const rule of allRules) {
    let triggered = false;

    switch (rule.triggerType) {
      case 'drug_interaction':
        triggered = matchesAny(rule.matchMedications, normalizedMeds);
        break;
      case 'condition':
        triggered = matchesAny(rule.matchConditions, normalizedConditions);
        break;
      case 'allergy':
        triggered = matchesAny(rule.matchAllergens, normalizedAllergies);
        break;
      case 'pregnancy':
        triggered = (isPregnant ?? false) || matchesAny(rule.matchConditions, normalizedConditions);
        break;
    }

    if (triggered) {
      for (const pid of rule.affectedProductIds) {
        blockedIds.add(pid);
      }
    }
  }

  // Filter by gender
  return allProducts.filter((p) => {
    if (blockedIds.has(p.id)) return false;
    if (userSex && p.gender !== 'any' && p.gender !== userSex) return false;
    return true;
  });
}
